import { RefObject } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { ITEM_SELECTOR, dragToScroll, findElementByKey } from '../utils/PRIVATE_collectionUtils';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { useReorderable } from '../hooks/PRIVATE_useReorderable';
import { scrollToVisible } from '../PRIVATE_List';
import { ListProps } from './List.types';

type useListViewReorderProps<K, D> = Pick<ListProps<K, D>, 'currentKey' | 'onReorder'> & {
  rootRef: RefObject<HTMLDivElement>;
  viewportConfig?: ViewportConfig;
};

export const useListViewReorder = <K extends string | number, D>({
  currentKey,
  onReorder,
  rootRef,
  viewportConfig
}: useListViewReorderProps<K, D>) => {
  const isReorderEnabled = onReorder != undefined;

  const [dragKey, setDragKey] = useState<K | undefined>(undefined);
  const { reorderProps, reorderContext, reorderInstructionsId, reorderInstructions } =
    useReorderable({
      onReorder,
      rootRef,
      itemSelector: ITEM_SELECTOR,
      isDisabled: !isReorderEnabled,
      columns: 1
    });

  // ensure the current item is scrolled into view during keyboard reordering,
  // disable that during mouse reordering
  const reorderScrollIntoView = useRef(false);
  useEffect(() => {
    if (isReorderEnabled && reorderScrollIntoView.current) {
      const rootElement = rootRef.current as HTMLElement;
      const elem = findElementByKey(rootElement, currentKey as K, ITEM_SELECTOR);
      if (elem) {
        const scroller = viewportConfig?.scroller() || rootElement;
        // make sure item is visible
        scrollToVisible(elem, scroller, 0);
        reorderScrollIntoView.current = false;
      }
    }
  });

  if (!isReorderEnabled) {
    return {
      reorderProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } as const;
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // listview only do keyboard reordering with arrow up/down keys
    if (
      isReorderEnabled &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
      (event.metaKey || event.ctrlKey) &&
      event.shiftKey
    ) {
      reorderScrollIntoView.current = true;
      reorderProps.onKeyDown?.(event);
    }
  };

  const handleDragOver = (event: DragEvent) => {
    if (dragKey !== undefined) {
      reorderScrollIntoView.current = false;
      dragToScroll(event, dragKey !== undefined, rootRef.current);
    }
  };

  const reorderEventProps = mergeProps({ onKeyDown: handleKeyDown, onDragOver: handleDragOver });

  const reorderItemProps = {
    currentKey: currentKey,
    dragKey,
    setDragKey,
    onReorder,
    rootRef
  };

  return {
    reorderProps: reorderEventProps,
    reorderItemProps,
    reorderContext,
    reorderInstructionsId,
    reorderInstructions
  } as const;
};
